import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

/**
 * Botão do design system (README §2 "Formas e medidas"): 38px no desktop,
 * 44px no celular (alvo de toque), raio 8, 13px semibold.
 *
 * - primary: 1 por página, na cor principal do tenant (texto por contraste).
 * - secondary: contorno fino — todas as outras ações.
 * - rail: na cor da barra — CTA do resumo lateral, "Tentar de novo".
 * - outline: contorno forte na cor do texto — ação do estado vazio.
 * - ghost: ícone/ação discreta. link: texto de ação (tom 5,9:1).
 */
export const dsButtonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-semibold transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ground disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-brand text-brand-ink hover:bg-brand/90",
        secondary: "border border-line bg-surface text-ink hover:bg-panel",
        rail: "bg-rail text-rail-ink hover:bg-rail/90",
        outline: "border border-ink bg-transparent text-ink hover:bg-panel",
        ghost: "text-ink-2 hover:bg-panel hover:text-ink",
        danger: "border border-line bg-surface text-alert-ink hover:bg-status-red",
        link: "text-action underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-11 px-3 text-sm md:h-7.5 md:px-2.5 md:text-ui",
        md: "h-11 px-4 text-sm md:h-9.5 md:px-3.5 md:text-ui",
        lg: "h-11 px-4 text-sm",
        icon: "size-11 md:size-9.5",
        "icon-sm": "size-11 md:size-7.5",
        inline: "h-auto p-0 text-ui",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export type DsButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof dsButtonVariants> & { asChild?: boolean };

export default function Button({
  className,
  variant,
  size,
  asChild = false,
  type,
  ...props
}: DsButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="ds-button"
      type={asChild ? undefined : (type ?? "button")}
      className={cn(dsButtonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
